import { CONTRACT_RATE, RouteCodes } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { FormSectionWithRows } from '@strata/react-form-section';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { ServicingRate } from '@strata/react-servicing-rate';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

export const LiveReferralDetails = ({ referralDetails = undefined }) => {
    const bids = useSelector((state) => state.referrals.bids[referralDetails?.referralId]);

    const supplierMaxBid =
        bids?.length > 0
            ? Math.min(
                  bids
                      .filter((bid) => bid.supplierNpi === referralDetails?.supplierNpi)
                      // Handle contract rate correctly
                      .map((bid) => (bid.bidAmt === null ? CONTRACT_RATE : bid.bidAmt)),
              )
            : undefined;

    const isPending =
        referralDetails?.referralStatus === ReferralStatusIndicator.Statuses.PENDINTAKE;

    /** Is/was the referral a Live Referral */
    const wentToLiveReferrals = referralDetails?.routeCode === RouteCodes.DEFAULT;

    return wentToLiveReferrals ? (
        <FormSectionWithRows
            sectionTitle="Live Referral Details"
            width="100%"
            isLoading={!referralDetails?.referralId || !bids}
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Start"
                        value={formatDate(referralDetails?.auctionStart, DateFormats.DATE_TIME)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="End"
                        value={formatDate(referralDetails?.auctionEnd, DateFormats.DATE_TIME)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label="Default Rate"
                        value={
                            <ServicingRate
                                servicingRate={referralDetails?.defaultRate}
                                referentPriceType={referralDetails?.referentPriceType}
                            />
                        }
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Winning Bid"
                        value={
                            <ServicingRate
                                servicingRate={referralDetails?.winningBidRate}
                                referentPriceType={referralDetails?.referentPriceType}
                            />
                        }
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Servicing Supplier Max Bid"
                        value={
                            <ServicingRate
                                servicingRate={supplierMaxBid}
                                referentPriceType={referralDetails?.referentPriceType}
                            />
                        }
                    />
                </>
            }
        />
    ) : (
        <FormSectionWithRows
            sectionTitle="Assignment Details"
            width="100%"
            isLoading={!referralDetails?.referralId}
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Assigned At"
                        value={formatDate(referralDetails?.contractedDate)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Referral Rate"
                        value={
                            <ServicingRate
                                servicingRate={referralDetails?.winningBidRate}
                                referentPriceType={referralDetails?.referentPriceType}
                                isPending={isPending}
                            />
                        }
                    />
                </>
            }
        />
    );
};

LiveReferralDetails.propTypes = {
    referralDetails: PropTypes.shape({
        referralId: PropTypes.string,
        auctionStart: PropTypes.string,
        auctionEnd: PropTypes.string,
        supplierNpi: PropTypes.string,
        winningBidRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        defaultRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        referentPriceType: PropTypes.string,
        referralStatus: PropTypes.oneOf(Object.values(ReferralStatusIndicator.Statuses)),
    }),
};
